import { useLingui } from "@lingui/react/macro"
import React, { forwardRef } from "react"
import { Virtuoso, VirtuosoHandle } from "react-virtuoso"

import HorizontalDivider from "../../../../design-system/horizontal-divider"
import ScrollContinuationGradient from "../../../../design-system/scroll-continuation-gradient"
import EmptyState from "../../../../shared-components/empty-state"
import { classNames } from "../../../../utils/class-names"
import useBreakpoint from "../../../../utils/hooks/use-breakpoint"
import CatalogTableRow from "./catalog-table-row"
import * as styles from "./index.module.scss"
import { CatalogItem } from "./types"

const DESKTOP_ITEM_HEIGHT = 63
const MOBILE_ITEM_HEIGHT = 89
const DESKTOP_MAX_HEIGHT = 945
const MOBILE_MAX_HEIGHT = 623
const MIN_ROWS_FOR_GRADIENT = Math.round(MOBILE_MAX_HEIGHT / MOBILE_ITEM_HEIGHT)
const SERVER_PRE_RENDERED_COUNT = 20

type Props = {
  catalogItems: CatalogItem[]
}

function CatalogTable({ catalogItems }: Props, ref: React.Ref<VirtuosoHandle>) {
  const { t } = useLingui()
  const { isSmallBreakpoint } = useBreakpoint()
  const maxHeight = isSmallBreakpoint ? MOBILE_MAX_HEIGHT : DESKTOP_MAX_HEIGHT
  const itemHeight = isSmallBreakpoint
    ? MOBILE_ITEM_HEIGHT
    : DESKTOP_ITEM_HEIGHT

  return (
    <div
      className={classNames(
        styles.container,
        maxHeight < catalogItems.length * itemHeight &&
          "always-visible-scroll-bar",
      )}
    >
      {!catalogItems.length ? (
        <div className={styles.emptyStateContainer}>
          <EmptyState
            title={t`No results found`}
            description={t`Change filters to see more results`}
            iconType="HelpWithPerson"
            iconColor="gold"
            data-testid="catalog-empty-state"
          />
        </div>
      ) : (
        <>
          {Number(catalogItems.length) > MIN_ROWS_FOR_GRADIENT && (
            <div className={styles.gradientContainer}>
              {/* Smaller scrolls for gradient of first item */}
              <ScrollContinuationGradient
                size="1rem"
                variant="top"
                zIndex={15}
              />
              <ScrollContinuationGradient size="5rem" variant="bottom" />
            </div>
          )}
          <Virtuoso
            ref={ref}
            data={catalogItems}
            initialItemCount={
              catalogItems.length < SERVER_PRE_RENDERED_COUNT
                ? catalogItems.length
                : SERVER_PRE_RENDERED_COUNT
            }
            style={{
              height: Math.min(maxHeight, catalogItems.length * itemHeight),
            }}
            itemContent={(index, item) => (
              <>
                {!!index && (
                  <div className={styles.divider}>
                    <HorizontalDivider />
                  </div>
                )}
                {!!item && (
                  <CatalogTableRow
                    {...item}
                    style={{
                      zIndex:
                        !index || index === catalogItems.length - 1 ? 10 : 0,
                    }}
                  />
                )}
              </>
            )}
          />
        </>
      )}
    </div>
  )
}

export default forwardRef(CatalogTable)
